import React, { useCallback } from "react";
import BluetoothSetup from "./BluetoothSetup";
import { connect } from "react-redux";
import { useState } from "react";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  displayPermanentSuccessNotification,
  displayErrorNotification,
  displayPermanentErrorNotification,
  updateMacAddress,
} from "../../../../../actions";
import { bleInfoTable } from "../../../../../utils/const";

/**
 * Chromium permits exactly one GATT operation in flight per device; anything
 * that overlaps is rejected with "GATT operation already in progress". Callers
 * several layers apart issue these, so serialize centrally rather than relying
 * on every call site to await in the right order.
 */
let gattQueue = Promise.resolve();

export const serializeGatt = (operation) => {
  const run = gattQueue.then(
    () => operation(),
    () => operation()
  );
  // Keep the queue alive after a failed operation without swallowing `run`.
  gattQueue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

const getValue = async (characteristic) =>
  serializeGatt(async () => {
    const enc = new TextDecoder("utf-8");
    const buf = await characteristic.readValue();
    return enc.decode(buf);
  });

const setValue = async (characteristic, value) =>
  serializeGatt(async () => {
    const enc = new TextEncoder();
    const msg = enc.encode(value);
    return characteristic.writeValueWithResponse(msg);
  });

/**
 * The device returns its scan results as one comma-separated string split over
 * five characteristics, each entry suffixed with four bytes of signal metadata.
 */
export const parseWifiList = (raw) => {
  if (!raw) return [];
  return raw
    .substring(0, raw.length - 1)
    .split(",")
    .map((entry) => entry.substring(0, entry.length - 4))
    .filter(Boolean);
};

/**
 * Synchronous reasons Web Bluetooth can never work in this browser/page.
 * Must stay synchronous: anything awaited before requestDevice() consumes the
 * user activation and the chooser is refused.
 */
export const getBluetoothBlocker = (nav = navigator, win = window) => {
  if (!nav?.bluetooth) {
    return win?.isSecureContext === false
      ? "This page is not running over HTTPS, so the browser disables Bluetooth. Open the site over https:// and try again."
      : "This browser does not support Web Bluetooth. Use Chrome or Edge on a desktop, or Chrome on Android. Every browser on iPhone/iPad (including Chrome) is unsupported.";
  }
  if (win?.isSecureContext === false) {
    return "This page is not running over HTTPS, so the browser disables Bluetooth. Open the site over https:// and try again.";
  }
  return null;
};

/** Turns a rejected requestDevice()/GATT promise into something a user can act on. */
export const describeBluetoothError = (err) => {
  const name = err?.name || "Error";
  const message = err?.message || String(err);

  if (name === "NotFoundError" && /cancel/i.test(message)) return null; // user closed the chooser
  if (name === "SecurityError") {
    return "The browser blocked the Bluetooth request. This usually means the page is not served over HTTPS, or the scan was not triggered directly by the button press.";
  }
  if (name === "NotFoundError") {
    return "No matching device was found. Check that the device is powered on, in pairing mode, and within range, and that this computer's Bluetooth is turned on.";
  }
  if (name === "NotSupportedError" || name === "TypeError") {
    return `Bluetooth is unavailable in this browser (${name}: ${message}).`;
  }
  return `${name}: ${message}`;
};

const Index = ({
  selectedDevice,
  deviceState,
  deviceName,
  filterStr = "Aga",
  macAddress,
  updateMacAddress,
  displayPermanentSuccessNotification,
  displayErrorNotification,
  displayPermanentErrorNotification,
}) => {
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [characteristics, setCharacteristics] = useState({});
  const [wifiList, setWifiList] = useState([]);
  const [ssid, setSSID] = useState("");
  const [psk, setPSK] = useState("");
  const [bluetoothState, setBluetoothState] = useState("disconnected");
  const history = useHistory();

  const characteristicsCached = useCallback(
    () =>
      characteristics?.scan?.list1 &&
      characteristics?.scan?.list2 &&
      characteristics?.scan?.list3 &&
      characteristics?.scan?.list4 &&
      characteristics?.scan?.list5 &&
      characteristics?.scan?.active &&
      characteristics?.connect?.wpa,
    [characteristics]
  );

  const cacheDeviceCharacteristics = useCallback(async (server) => {
    console.log('Caching device characteristics');
    const gattChars = {};

    // Sequential, not Promise.all: service discovery is a GATT operation too.
    for (const [serviceName, serviceUuid] of Object.entries(
      bleInfoTable.services
    )) {
      console.log(`Caching ${serviceName}`);
      const service = await serializeGatt(() =>
        server.getPrimaryService(serviceUuid)
      );
      gattChars[serviceName] = await getCharactersticsGivenService(
        service,
        serviceName
      );
      console.log('Characteristics for', serviceName, ':', gattChars[serviceName]);
    }

    console.log('Final characteristics object:', gattChars);
    setCharacteristics(gattChars);
    return gattChars;
  }, []);

  const disconnectDevice = useCallback(
    async (device) => {
      device = device || bluetoothDevice;
      if (device) {
        if (device.gatt.connected) {
          device.gatt.disconnect();
        }
      }
      setBluetoothDevice(null);
      setCharacteristics({});
      setBluetoothState("disconnected");
    },
    [bluetoothDevice]
  );

  useEffect(() => {
    const checkCompletionStatus = async () => {
      if (bluetoothState !== "disconnected" && deviceState === "BOOTING") {
        displayPermanentSuccessNotification(
          `${deviceName} was successfully connected. Device will restart.`
        );
        history.goBack();
      }
    };
    checkCompletionStatus();
  }, [
    bluetoothState,
    deviceState,
    deviceName,
    displayPermanentSuccessNotification,
    history,
  ]);

  const reconnectDevice = async (bluetoothDevice) => {
    if (!bluetoothDevice) {
      console.error('No device provided for reconnection');
      displayErrorNotification('Cannot reconnect: no device available');
      return;
    }

    console.log('Reconnecting Bluetooth device...');

    let server;
    for (let i = 0; i < 5; i++) {
      try {
        server = bluetoothDevice.gatt.connected
          ? bluetoothDevice.gatt
          : await bluetoothDevice.gatt.connect();
        console.log(server);
        if (server.connected) {
          break;
        } else {
          console.log("server completed but not connected");
        }
      } catch (error) {
        console.log(error)
        console.log(`Gatt connection failed, retrying (${i})...`);
      }
    }

    if (!server || !server.connected) {
      displayErrorNotification(
        `There was an error connecting to ${deviceName}. Please refresh the page and try again.`
      );
      disconnectDevice();
      return;
    }

    console.log('Attempting to cache characteristics...');
    let chars;
    try {
      chars = await cacheDeviceCharacteristics(server);
    } catch (err) {
      console.error('Characteristic caching failed:', err);

      if (!bluetoothDevice.gatt.connected) {
        reconnectDevice(bluetoothDevice);
      }
      return;
    }

    chars.scan.active.startNotifications().then((_) => {
      chars.scan.active.addEventListener(
        "characteristicvaluechanged",
        async function handler(event) {
          const value = event.target?.value?.getInt32(0, true);
          console.log(`wifi/scan updated value to:`);
          console.log(value);

          if (value) {
            this.removeEventListener("characteristicvaluechanged", handler);
            displayPermanentSuccessNotification(
              `${deviceName} was successfully connected. Device will restart.`
            );
            disconnectDevice();
            history.goBack();
          } else {
            console.log(bluetoothState);
            displayErrorNotification(
              `Wifi connection unsuccessful. Please check password.`
            );
            setBluetoothState("connected");
          }
        }
      );
    });
    console.log("reconnected");
    setBluetoothState("connected");
  };

  useEffect(() => {
    const areCharachteristicsCached = characteristicsCached();
    if (
      bluetoothState !== "connecting" &&
      bluetoothState !== "loading" &&
      bluetoothDevice?.gatt &&
      (!bluetoothDevice?.gatt?.connected || !areCharachteristicsCached)
    ) {
      console.log('i should try to connect the device')
      setCharacteristics({});
      reconnectDevice();
    }
    // eslint-disable-next-line
  }, [
    bluetoothDevice,
    bluetoothDevice?.gatt?.connected,
    bluetoothState,
    cacheDeviceCharacteristics,
    characteristicsCached,
    deviceName,
    disconnectDevice,
    displayErrorNotification,
    displayPermanentSuccessNotification,
    history,
  ]);

  /**
  * Handles WiFi scanning and MAC address retrieval
  */
  useEffect(() => {
    const findMac = async () => {
      console.log("Searching for mac");
      // The mac service is best-effort: a missing one must not block setup.
      if (!characteristics?.mac?.addr) {
        console.warn("No mac characteristic cached; skipping mac lookup");
        return;
      }
      try {
        const mac = await getValue(characteristics.mac.addr);
        console.log("mac", mac);
        updateMacAddress(selectedDevice, mac);
      } catch (err) {
        console.error("Mac scanning error:", err);
      }
    };

    const findWifi = async () => {
      console.log("Searching for wifi");

      let wifiListStr;
      try {
        const parts = [];
        for (const key of ["list1", "list2", "list3", "list4", "list5"]) {
          parts.push(await getValue(characteristics.scan[key]));
        }
        wifiListStr = parts.join("");
      } catch (err) {
        console.error("Wifi scanning error:", err);
        displayPermanentErrorNotification(
          `Could not read the wifi list from ${deviceName}. ${
            describeBluetoothError(err) || "The connection was interrupted."
          } Please refresh the page and try again.`
        );
        disconnectDevice();
        return;
      }

      const wl = parseWifiList(wifiListStr);
      if (!wl.length) {
        // Reads succeeded and came back empty - the device scanned and saw
        // nothing, which is a different problem from a failed read.
        displayPermanentErrorNotification(
          `${deviceName} did not report any wifi networks in range. Check that it is within range of the access point, then press Rescan.`
        );
        disconnectDevice();
        return;
      }
      setWifiList(wl);
    };

    const readDeviceInfo = async () => {
      if (!characteristicsCached()) {
        setBluetoothState("loading");
        return;
      }
      // Strictly sequential: these share one GATT connection.
      await findMac();
      await findWifi();
    };

    if (bluetoothState === "connected" && wifiList.length === 0) {
      readDeviceInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wifiList,
    bluetoothState,
    characteristics,
    characteristicsCached,
    deviceName,
    disconnectDevice,
    displayPermanentErrorNotification,
    selectedDevice,
    updateMacAddress,
  ]);

  const requestDevice = async () => {
    let requestTimer = null;

    try {
      requestTimer = setTimeout(() => {
        requestTimer = null;
        displayErrorNotification(
          `It is taking an unusually long time to connect to ${deviceName}. You may want to refresh the page and try again.`
        );
      }, 59 * 1000);

      const device = await navigator.bluetooth
        .requestDevice({
          filters: [
            { namePrefix: filterStr },
            { namePrefix: filterStr?.toLowerCase() },
          ],
          optionalServices: Object.values(bleInfoTable.services),
        })

      if (device) {
        console.log('Device discovered:', device.name);

        setBluetoothState('loading');
        setBluetoothDevice(device);

        // Initiate connection process
        await reconnectDevice(device);
      }

      return device;
    } catch (err) {
      console.error('Bluetooth device request failed:', err);

      setBluetoothState("disconnected");

      const detail = describeBluetoothError(err);
      if (!detail) return; // user dismissed the chooser themselves

      // Safe to await now that the chooser is closed: an adapter that is off or
      // missing is by far the most common cause of an instant rejection.
      let adapterAvailable = true;
      try {
        adapterAvailable = await navigator.bluetooth.getAvailability();
      } catch (availabilityErr) {
        console.error('getAvailability() failed:', availabilityErr);
      }

      displayPermanentErrorNotification(
        adapterAvailable
          ? `Could not connect to ${deviceName}. ${detail}`
          : `Could not connect to ${deviceName}. This computer has no Bluetooth adapter available, or Bluetooth is turned off. Turn Bluetooth on and try again. (${detail})`
      );
    } finally {
      if (requestTimer) clearTimeout(requestTimer);
    }
  };

  const getCharactersticsGivenService = async (service, serviceName) => {
    const serviceChars = {};
    for (const [charName, charUuid] of Object.entries(
      bleInfoTable.characteristics[serviceName]
    )) {
      console.log(`Caching ${serviceName}/${charName}`);
      serviceChars[charName] = await serializeGatt(() =>
        service.getCharacteristic(charUuid)
      );
    }
    return serviceChars;
  };

  const onScanButtonPressed = async () => {
    // Synchronous so the click's user activation survives into requestDevice().
    const blocker = getBluetoothBlocker();
    if (blocker) {
      displayPermanentErrorNotification(blocker);
      return;
    }
    if (!bluetoothDevice) await requestDevice();
  };

  const onConnectButtonPressed = async () => {
    try {
      if (!characteristicsCached()) {
        console.log("Could not find wpa characteristic");
        setBluetoothState("loading");
        return;
      }
      setBluetoothState("connecting");

      console.log("Set up connection timeout");
      const prefix =
        ssid.length < 10
          ? ssid.length
          : String.fromCharCode("a".charCodeAt(0) + (ssid.length - 10));
      const buff = prefix + ssid + psk;

      let ret;
      try {
        ret = await setValue(characteristics.connect.wpa, buff);
      } catch (err) {
        console.error("connect.wpa failed: " + err);
      }
      console.log(`connect.wpa finished with ${ret}`);
    } catch (error) {
      console.log(`Error: ${error}`);
    }
  };

  const onChangeSSID = (obj) => {
    if (obj && obj.target && typeof obj.target.value === "string")
      setSSID(obj.target.value);
    else setSSID("");
  };

  const onChangePSK = (obj) => {
    if (obj && obj.target && typeof obj.target.value === "string")
      setPSK(obj.target.value);
    else setPSK("");
  };
  const onRescanButtonPressed = () => {
    if (bluetoothState === "connected") {
      setWifiList([]);
    }
  };
  return (
    <BluetoothSetup
      bluetoothState={bluetoothState}
      handleChangeSSID={onChangeSSID}
      handleChangePSK={onChangePSK}
      handleConnectButtonPressed={onConnectButtonPressed}
      handleScanButtonPressed={onScanButtonPressed}
      handleRescanWifiButtonPressed={onRescanButtonPressed}
      wifiList={wifiList}
      ssid={ssid}
      psk={psk}
    />
  );
};

export const mapStateToProps = (state) => {
  const { selectedDevice, ...devices } = state.devices;
  if (!selectedDevice) return {};
  const {
    stateMachine: deviceState,
    displayName: deviceName,
    macAddress = null,
  } = devices?.[selectedDevice];
  let filterStr = "Aga"; // maintain backwards compatability with older versions
  // if (firmwareVersion && compareVersions(firmwareVersion, "v1.5.0") >= 0) {
  //   const filterStrTmp = selectedDevice.split("-");
  //   filterStr = filterStrTmp[filterStrTmp.length - 1];
  // }
  return {
    selectedDevice,
    macAddress,
    deviceState,
    deviceName,
    filterStr,
  };
};

export default connect(mapStateToProps, {
  displayPermanentSuccessNotification,
  displayErrorNotification,
  displayPermanentErrorNotification,
  updateMacAddress,
})(Index);
